import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  TextField,
  Button,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  FilterList,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  CheckCircle,
  Error as ErrorIcon,
  Inbox,
  InfoOutlined,
  ExpandMore,
  FolderOpen,
  Description,
} from '@mui/icons-material';
import axios from 'axios';
import { CONFIG } from '../../config/config';
import { getToken } from '../../services/auth';
import { getReviewEntities, ReviewEntity, getComputedMetricsMetadata, type ComputedMetricMetadataResponse } from '../../services/api';
import { getAllReviewDimensions, ReviewDimension } from '../../services/reviewDimensionService';
import MetricTrendDialog from '../../components/MetricTrendDialog/MetricTrendDialog';
import {
  formatDisplayDate,
  formatDisplayDateTime,
  withGridDateColumns,
} from '../../utils/dateUtils';
import {
  formatMetricLevelLabel,
  isProjectLevelMetric,
  PROJECT_METRIC_LEVEL,
  sortMetricLevelTypes,
} from '../../utils/metricLevelUtils';

interface TreeOption {
  entity: ReviewEntity;
  depth: number;
  hasChildren: boolean;
}

function buildEntityTree(entities: ReviewEntity[]): TreeOption[] {
  const idMap = new Map<number, ReviewEntity>();
  entities.forEach((e) => idMap.set(e.id, e));

  const childrenMap = new Map<number, ReviewEntity[]>();
  const rootEntities: ReviewEntity[] = [];

  entities.forEach((e) => {
    const parentIds = e.parent_ids?.filter((pid) => idMap.has(pid)) || [];
    if (parentIds.length === 0) {
      rootEntities.push(e);
    } else {
      const pid = parentIds[0];
      if (!childrenMap.has(pid)) childrenMap.set(pid, []);
      childrenMap.get(pid)!.push(e);
    }
  });

  const sortByName = (a: ReviewEntity, b: ReviewEntity) => a.name.localeCompare(b.name);
  rootEntities.sort(sortByName);
  childrenMap.forEach((children) => children.sort(sortByName));

  const result: TreeOption[] = [];
  const flatten = (entity: ReviewEntity, depth: number) => {
    const children = childrenMap.get(entity.id) || [];
    result.push({ entity, depth, hasChildren: children.length > 0 });
    children.forEach((child) => flatten(child, depth + 1));
  };
  rootEntities.forEach((root) => flatten(root, 0));

  return result;
}

type ValueType = 'Percentage' | 'Amount' | 'Ratio' | 'UOM' | 'Ratings';

interface ComputedMetric {
  computed_metric_id: number;
  metric_date: string;
  metric_type: string;
  metric_name: string;
  metric_value: number;
  metric_identifier_type?: string | null;
  metric_identifier_name: string | null;
  value_type?: ValueType | null;
  unit_of_measure?: string | null;
  is_threshold_violated: boolean;
  lower_bound: number | null;
  upper_bound: number | null;
  target_value?: number | null;
  threshold_direction?: string | null;
  review_entity_name: string;
  review_entity_type: any;
}

type DimLoadStatus = 'idle' | 'loading' | 'loaded' | 'empty' | 'error';

interface DimensionState {
  status: DimLoadStatus;
  metrics: ComputedMetric[];
}

const STATUS_ORDER: Record<DimLoadStatus, number> = {
  loaded: 0,
  loading: 1,
  idle: 2,
  error: 3,
  empty: 4,
};

const formatMetricValue = (m: ComputedMetric) => {
  const v = Number(m.metric_value);

  switch (m.value_type) {
    case 'Percentage': {
      const pct = (v * 100).toFixed(2);
      // When UoM is shown separately (e.g. "%"), omit the suffix for uniformity
      return m.unit_of_measure ? pct : `${pct}%`;
    }
    case 'Amount':
      return `₹${v.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    case 'Ratings':
      return `${v.toFixed(2)} / 5`;
    case 'Ratio':
    case 'UOM':
    default:
      return v.toFixed(2);
  }
};

const DimStatusIcon = ({ status }: { status: DimLoadStatus }) => {
  switch (status) {
    case 'loading':
      return <CircularProgress size={14} sx={{ ml: 0.75 }} />;
    case 'loaded':
      return <CheckCircle sx={{ ml: 0.75, fontSize: 16, color: 'success.main' }} />;
    case 'empty':
      return <Inbox sx={{ ml: 0.75, fontSize: 16, color: 'text.disabled' }} />;
    case 'error':
      return <ErrorIcon sx={{ ml: 0.75, fontSize: 16, color: 'error.main' }} />;
    default:
      return null;
  }
};

interface ComputedMetricsContentProps {
  initialEntityId?: number | null;
  hideEntitySelectors?: boolean;
}

const ComputedMetricsContent: React.FC<ComputedMetricsContentProps> = ({
  initialEntityId = null,
  hideEntitySelectors = false,
}) => {
  const [reviewEntities, setReviewEntities] = useState<ReviewEntity[]>([]);
  const [reviewDimensions, setReviewDimensions] = useState<ReviewDimension[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [selectedEntityId, setSelectedEntityId] = useState<number | ''>(initialEntityId || '');
  const [activeDimName, setActiveDimName] = useState('');
  const [metricDate, setMetricDate] = useState('');
  const [selectedLevelType, setSelectedLevelType] = useState<string>('ALL');
  const [dimensionData, setDimensionData] = useState<Record<string, DimensionState>>({});
  const abortRef = useRef<AbortController | null>(null);
  const [metadataDialogOpen, setMetadataDialogOpen] = useState(false);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<ComputedMetricMetadataResponse | null>(null);
  const [filteredMetaDialogOpen, setFilteredMetaDialogOpen] = useState(false);
  const [filteredMetaLoading, setFilteredMetaLoading] = useState(false);
  const [filteredMetaError, setFilteredMetaError] = useState<string | null>(null);
  const [filteredMetadata, setFilteredMetadata] = useState<ComputedMetricMetadataResponse | null>(null);
  const [trendDialogOpen, setTrendDialogOpen] = useState(false);
  const [selectedMetricForTrend, setSelectedMetricForTrend] = useState<ComputedMetric | null>(null);
  const [trendData, setTrendData] = useState<Array<{
    metric_date: string;
    metric_value: number;
    is_threshold_violated: boolean;
    lower_bound: number | null;
    upper_bound: number | null;
    target_value?: number | null;
    threshold_direction?: string | null;
  }>>([]);
  const [trendLoading, setTrendLoading] = useState(false);
  const [filterEntityType, setFilterEntityType] = useState('');

  const handleOpenMetadata = async () => {
    setMetadataDialogOpen(true);
    setMetadataLoading(true);
    setMetadataError(null);
    try {
      const res = await getComputedMetricsMetadata();
      setMetadata(res.data);
    } catch {
      setMetadataError('Failed to load computed metrics metadata');
    } finally {
      setMetadataLoading(false);
    }
  };

  const handleOpenFilteredMetadata = async () => {
    if (!selectedEntityId) return;
    setFilteredMetaDialogOpen(true);
    setFilteredMetaLoading(true);
    setFilteredMetaError(null);
    try {
      const res = await getComputedMetricsMetadata();
      const all = res.data;
      const filtered: ComputedMetricMetadataResponse = {};
      for (const [dim, entries] of Object.entries(all)) {
        const match = entries.filter((e) => e.review_entity_id === selectedEntityId);
        if (match.length > 0) filtered[dim] = match;
      }
      setFilteredMetadata(filtered);
    } catch {
      setFilteredMetaError('Failed to load computed metrics metadata');
    } finally {
      setFilteredMetaLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const loadOptions = async () => {
      try {
        const [entitiesRes, dimensions] = await Promise.all([
          getReviewEntities(),
          getAllReviewDimensions(),
        ]);
        if (cancelled) return;
        setReviewEntities(entitiesRes.data || []);
        setReviewDimensions(dimensions || []);
      } catch {
        if (!cancelled) setOptionsError('Failed to load review entities or dimensions');
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    };
    loadOptions();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!hideEntitySelectors && initialEntityId) {
      setSelectedEntityId(initialEntityId);
    }
  }, [initialEntityId, hideEntitySelectors]);

  const selectedEntity = useMemo(
    () => reviewEntities.find((e) => e.id === selectedEntityId),
    [reviewEntities, selectedEntityId]
  );

  const applicableDimensions = useMemo(() => {
    const dimNames = selectedEntity?.review_entity_type?.associated_review_dimension_names;
    if (!dimNames || dimNames.length === 0) return reviewDimensions;
    return reviewDimensions.filter((d) => dimNames.includes(d.name));
  }, [selectedEntity, reviewDimensions]);

  const sortedDimensions = useMemo(() => {
    return [...applicableDimensions].sort((a, b) => {
      const sa = dimensionData[a.name]?.status || 'idle';
      const sb = dimensionData[b.name]?.status || 'idle';
      return STATUS_ORDER[sa] - STATUS_ORDER[sb];
    });
  }, [applicableDimensions, dimensionData]);

  const activeTabIndex = useMemo(() => {
    const idx = sortedDimensions.findIndex((d) => d.name === activeDimName);
    return idx >= 0 ? idx : 0;
  }, [sortedDimensions, activeDimName]);

  const handleTabChange = (_: React.SyntheticEvent, newIndex: number) => {
    const dim = sortedDimensions[newIndex];
    if (dim) setActiveDimName(dim.name);
  };

  useEffect(() => {
    setDimensionData({});
    setActiveDimName('');
    setMetricDate('');
    if (!selectedEntityId || applicableDimensions.length === 0) return;

    setActiveDimName(applicableDimensions[0].name);
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const token = getToken();

    const initial: Record<string, DimensionState> = {};
    applicableDimensions.forEach((dim) => {
      initial[dim.name] = { status: 'loading', metrics: [] };
    });
    setDimensionData(initial);

    applicableDimensions.forEach((dim) => {
      axios
        .get(`${CONFIG.SERVER_URL}/get_computed_metrics_by_entity_and_dimension`, {
          params: { review_entity_id: selectedEntityId, review_dimension: dim.name },
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        })
        .then((res) => {
          const metrics: ComputedMetric[] = res.data || [];
          setDimensionData((prev) => ({
            ...prev,
            [dim.name]: {
              status: metrics.length > 0 ? 'loaded' : 'empty',
              metrics,
            },
          }));
        })
        .catch((err) => {
          if (axios.isCancel(err)) return;
          setDimensionData((prev) => ({
            ...prev,
            [dim.name]: { status: 'error', metrics: [] },
          }));
        });
    });

    return () => { controller.abort(); };
  }, [selectedEntityId, applicableDimensions]);

  const entityTypeOptions = useMemo(() => {
    const types = new Set(
      reviewEntities
        .map((e) => e.review_entity_type?.display_label)
        .filter(Boolean)
    );
    return Array.from(types).sort() as string[];
  }, [reviewEntities]);

  const filteredEntities = useMemo(() => {
    const list = filterEntityType
      ? reviewEntities.filter((e) => e.review_entity_type?.display_label === filterEntityType)
      : reviewEntities;
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [reviewEntities, filterEntityType]);

  const treeOptions = useMemo(() => buildEntityTree(filteredEntities), [filteredEntities]);

  useEffect(() => {
    if (!hideEntitySelectors) {
      setSelectedEntityId('');
    }
  }, [filterEntityType, hideEntitySelectors]);

  useEffect(() => {
    if (hideEntitySelectors && initialEntityId) {
      setSelectedEntityId(initialEntityId);
    }
  }, [hideEntitySelectors, initialEntityId, optionsLoading]);

  const currentDimState = dimensionData[activeDimName];
  const metrics = currentDimState?.metrics || [];

  const metricDates = useMemo(() => {
    return Array.from(new Set(metrics.map((m) => m.metric_date)))
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
  }, [metrics]);

  useEffect(() => {
    if (metricDates.length > 0) {
      // Prefer showing full hierarchy when multiple period ends exist
      setMetricDate(metricDates.length > 1 ? '' : metricDates[0]);
    } else {
      setMetricDate('');
    }
  }, [activeDimName, metricDates.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePreviousDate = () => {
    const idx = metricDates.indexOf(metricDate);
    if (idx < metricDates.length - 1) setMetricDate(metricDates[idx + 1]);
  };
  const handleNextDate = () => {
    const idx = metricDates.indexOf(metricDate);
    if (idx > 0) setMetricDate(metricDates[idx - 1]);
  };
  const isPreviousDisabled = () => {
    const idx = metricDates.indexOf(metricDate);
    return idx === metricDates.length - 1 || metricDates.length === 0;
  };
  const isNextDisabled = () => {
    const idx = metricDates.indexOf(metricDate);
    return idx === 0 || idx === -1 || metricDates.length === 0;
  };

  const handleOpenTrend = (metric: ComputedMetric) => {
    setSelectedMetricForTrend(metric);
    setTrendDialogOpen(true);
    setTrendLoading(true);

    const historicalData = metrics
      .filter(
        (m) =>
          m.metric_name === metric.metric_name &&
          m.review_entity_name === metric.review_entity_name &&
          (m.metric_identifier_type || null) === (metric.metric_identifier_type || null) &&
          m.metric_identifier_name === metric.metric_identifier_name &&
          m.metric_type === metric.metric_type
      )
      .map((m) => ({
        metric_date: m.metric_date,
        metric_value: m.metric_value,
        is_threshold_violated: m.is_threshold_violated,
        lower_bound: m.lower_bound,
        upper_bound: m.upper_bound,
        target_value: m.target_value,
        threshold_direction: m.threshold_direction,
      }));

    setTrendData(historicalData);
    setTrendLoading(false);
  };

  const handleCloseTrend = () => {
    setTrendDialogOpen(false);
    setSelectedMetricForTrend(null);
    setTrendData([]);
  };

  const filteredRows = useMemo(
    () => metrics.filter((m) => (!metricDate || m.metric_date === metricDate)),
    [metrics, metricDate]
  );

  const availableLevelTypes = useMemo(() => {
    const types = new Set<string>();
    filteredRows.forEach((m) => {
      if (isProjectLevelMetric(m)) {
        types.add(PROJECT_METRIC_LEVEL);
      } else {
        types.add((m.metric_identifier_type || 'UNKNOWN').toUpperCase());
      }
    });
    return sortMetricLevelTypes(Array.from(types));
  }, [filteredRows]);

  useEffect(() => {
    if (selectedLevelType !== 'ALL' && !availableLevelTypes.includes(selectedLevelType)) {
      setSelectedLevelType('ALL');
    }
  }, [availableLevelTypes, selectedLevelType]);

  const levelFilteredRows = useMemo(() => {
    if (selectedLevelType === 'ALL') return filteredRows;
    if (selectedLevelType === PROJECT_METRIC_LEVEL) {
      return filteredRows.filter((m) => isProjectLevelMetric(m));
    }
    return filteredRows.filter(
      (m) =>
        !isProjectLevelMetric(m) &&
        (m.metric_identifier_type || 'UNKNOWN').toUpperCase() === selectedLevelType
    );
  }, [filteredRows, selectedLevelType]);

  const projectMetrics = useMemo(
    () =>
      levelFilteredRows
        .filter((m) => isProjectLevelMetric(m))
        .sort((a, b) => a.metric_name.localeCompare(b.metric_name)),
    [levelFilteredRows]
  );

  const scopedMetrics = useMemo(
    () =>
      levelFilteredRows
        .filter((m) => !isProjectLevelMetric(m))
        .sort((a, b) => {
          const typeCmp = (a.metric_identifier_type || '').localeCompare(
            b.metric_identifier_type || ''
          );
          if (typeCmp !== 0) return typeCmp;
          const nameCmp = (a.metric_identifier_name || '').localeCompare(
            b.metric_identifier_name || ''
          );
          if (nameCmp !== 0) return nameCmp;
          return a.metric_name.localeCompare(b.metric_name);
        }),
    [levelFilteredRows]
  );

  const columns: GridColDef[] = withGridDateColumns([
    { field: 'metric_date', headerName: 'Metric Date', flex: 0.7 },
    {
      field: 'metric_identifier_type',
      headerName: 'Metric Level',
      flex: 0.8,
      renderCell: (params) => {
        const row = params.row as ComputedMetric;
        return formatMetricLevelLabel(row.metric_identifier_type);
      },
    },
    {
      field: 'metric_identifier_name',
      headerName: 'Level Instance',
      flex: 1,
      renderCell: (params) => params.value ?? '',
    },
    { field: 'metric_name', headerName: 'Metric Name', flex: 1 },
    {
      field: 'metric_value',
      headerName: 'Value',
      flex: 0.6,
      renderCell: (params) => {
        const row = params.row as ComputedMetric;
        return (
          <Typography color={row.is_threshold_violated ? 'error.main' : 'success.main'}>
            {formatMetricValue(row)}
          </Typography>
        );
      },
    },
    {
      field: 'unit_of_measure',
      headerName: 'UoM',
      flex: 0.4,
      renderCell: (params) => (params.value as string) || '—',
    },
    {
      field: 'trend',
      headerName: 'Trend',
      flex: 0.4,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as ComputedMetric;
        return (
          <Tooltip title="View Trend">
            <IconButton size="small" color="primary" onClick={() => handleOpenTrend(row)}>
              <TrendingUp fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ]);

  const entitySelected = selectedEntityId !== '';
  const hasDimensions = sortedDimensions.length > 0;

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
        <Tooltip title="View Metric Input Metadata">
          <IconButton color="primary" onClick={handleOpenMetadata}>
            <InfoOutlined />
          </IconButton>
        </Tooltip>
      </Box>

      <Dialog open={metadataDialogOpen} onClose={() => setMetadataDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Computed Metrics Metadata</DialogTitle>
        <DialogContent dividers>
          {metadataLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : metadataError ? (
            <Alert severity="error">{metadataError}</Alert>
          ) : metadata && Object.keys(metadata).length > 0 ? (
            Object.entries(metadata)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([dimension, entries]) => (
                <Accordion key={dimension} defaultExpanded={false}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                      {dimension.replace(/_/g, ' ')}
                    </Typography>
                    <Chip label={`${entries.length} entities`} size="small" sx={{ ml: 1 }} />
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><Typography sx={{ fontWeight: 'bold' }}>Entity Name</Typography></TableCell>
                            <TableCell><Typography sx={{ fontWeight: 'bold' }}>Total Metrics</Typography></TableCell>
                            <TableCell><Typography sx={{ fontWeight: 'bold' }}>Earliest Date</Typography></TableCell>
                            <TableCell><Typography sx={{ fontWeight: 'bold' }}>Latest Date</Typography></TableCell>
                            <TableCell><Typography sx={{ fontWeight: 'bold' }}>Last Loaded</Typography></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {entries.map((entry) => (
                            <TableRow key={entry.review_entity_id}>
                              <TableCell>{entry.review_entity_name || entry.review_entity_id}</TableCell>
                              <TableCell>{entry.total_metrics}</TableCell>
                              <TableCell>{entry.earliest_metric_date ? formatDisplayDate(entry.earliest_metric_date) : 'N/A'}</TableCell>
                              <TableCell>{entry.latest_metric_date ? formatDisplayDate(entry.latest_metric_date) : 'N/A'}</TableCell>
                              <TableCell>{entry.last_loaded_date ? formatDisplayDateTime(entry.last_loaded_date) : 'N/A'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              ))
          ) : (
            <Typography variant="body2" color="text.secondary">No metadata available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMetadataDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {optionsLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 4 }}>
          <CircularProgress size={24} />
          <Typography>Loading filters...</Typography>
        </Box>
      ) : optionsError ? (
        <Alert severity="error">{optionsError}</Alert>
      ) : (
        <>
          {!hideEntitySelectors && (
            <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <FilterList sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Select Review Entity</Typography>
              </Box>

              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="entity-type-label">Entity Type</InputLabel>
                    <Select
                      labelId="entity-type-label"
                      value={filterEntityType}
                      label="Entity Type"
                      onChange={(e) => setFilterEntityType(e.target.value as string)}
                    >
                      <MenuItem value="">
                        <em>All Types</em>
                      </MenuItem>
                      {entityTypeOptions.map((t) => (
                        <MenuItem key={t} value={t}>{t}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    size="small"
                    options={treeOptions}
                    value={treeOptions.find((o) => o.entity.id === selectedEntityId) || null}
                    onChange={(_, newValue) => setSelectedEntityId(newValue ? newValue.entity.id : '')}
                    getOptionLabel={(option) => option.entity.name}
                    renderOption={(props, option) => (
                      <li {...props} key={option.entity.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', pl: option.depth * 2.5 }}>
                          {option.hasChildren ? (
                            <FolderOpen sx={{ fontSize: 18, mr: 0.75, color: 'warning.main' }} />
                          ) : (
                            <Description sx={{ fontSize: 18, mr: 0.75, color: 'action.active' }} />
                          )}
                          <Box>
                            <Typography variant="body2">{option.entity.name}</Typography>
                            {option.entity.review_entity_type?.display_label && (
                              <Typography variant="caption" color="text.secondary">
                                {option.entity.review_entity_type.display_label}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </li>
                    )}
                    filterOptions={(options, { inputValue }) => {
                      if (!inputValue) return options;
                      const lower = inputValue.toLowerCase();
                      const matchIds = new Set<number>();
                      options.forEach((o) => {
                        if (o.entity.name.toLowerCase().includes(lower)) {
                          matchIds.add(o.entity.id);
                          o.entity.parent_ids?.forEach((pid) => matchIds.add(pid));
                        }
                      });
                      return options.filter((o) => matchIds.has(o.entity.id));
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Review Entity" />
                    )}
                    isOptionEqualToValue={(option, value) => option.entity.id === value.entity.id}
                    ListboxProps={{ sx: { maxHeight: 400 } }}
                  />
                </Grid>
              </Grid>
            </Paper>
          )}

          {!entitySelected && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                Select a Review Entity above to load computed metrics across all dimensions.
              </Typography>
            </Paper>
          )}

          {entitySelected && hasDimensions && (
            <>
              <Paper sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Tabs
                    sx={{ flex: 1 }}
                    value={activeTabIndex}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    {sortedDimensions.map((dim) => {
                      const st = dimensionData[dim.name]?.status || 'idle';
                      return (
                        <Tab
                          key={dim.name}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {dim.name}
                              <DimStatusIcon status={st} />
                            </Box>
                          }
                        />
                      );
                    })}
                  </Tabs>
                  <Tooltip title="View Metadata for Selected Entity">
                    <IconButton color="primary" onClick={handleOpenFilteredMetadata} sx={{ mr: 1 }}>
                      <InfoOutlined />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>

              <Dialog open={filteredMetaDialogOpen} onClose={() => setFilteredMetaDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Computed Metrics Metadata — {selectedEntity?.name}</DialogTitle>
                <DialogContent dividers>
                  {filteredMetaLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : filteredMetaError ? (
                    <Alert severity="error">{filteredMetaError}</Alert>
                  ) : filteredMetadata && Object.keys(filteredMetadata).length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><Typography sx={{ fontWeight: 'bold' }}>Dimension</Typography></TableCell>
                            <TableCell><Typography sx={{ fontWeight: 'bold' }}>Total Metrics</Typography></TableCell>
                            <TableCell><Typography sx={{ fontWeight: 'bold' }}>Earliest Date</Typography></TableCell>
                            <TableCell><Typography sx={{ fontWeight: 'bold' }}>Latest Date</Typography></TableCell>
                            <TableCell><Typography sx={{ fontWeight: 'bold' }}>Last Loaded</Typography></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.entries(filteredMetadata)
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([dim, entries]) =>
                              entries.map((entry) => (
                                <TableRow key={dim}>
                                  <TableCell sx={{ textTransform: 'capitalize' }}>{dim.replace(/_/g, ' ')}</TableCell>
                                  <TableCell>{entry.total_metrics}</TableCell>
                                  <TableCell>{entry.earliest_metric_date ? formatDisplayDate(entry.earliest_metric_date) : 'N/A'}</TableCell>
                                  <TableCell>{entry.latest_metric_date ? formatDisplayDate(entry.latest_metric_date) : 'N/A'}</TableCell>
                                  <TableCell>{entry.last_loaded_date ? formatDisplayDateTime(entry.last_loaded_date) : 'N/A'}</TableCell>
                                </TableRow>
                              ))
                            )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No computed metrics metadata available for this entity.</Typography>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setFilteredMetaDialogOpen(false)}>Close</Button>
                </DialogActions>
              </Dialog>

              {currentDimState?.status === 'loading' && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <CircularProgress />
                </Box>
              )}

              {currentDimState?.status === 'error' && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Failed to load metrics for dimension "{activeDimName}".
                </Alert>
              )}

              {currentDimState?.status === 'empty' && (
                <Alert severity="info">
                  No computed metrics found for dimension "{activeDimName}".
                </Alert>
              )}

              {currentDimState?.status === 'loaded' && (
                <>
                  <Paper sx={{ p: 2, mb: 3 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Tooltip title="Previous date">
                            <span>
                              <IconButton
                                size="small"
                                onClick={handlePreviousDate}
                                disabled={isPreviousDisabled()}
                                sx={{ border: '1px solid', borderColor: 'divider' }}
                              >
                                <ChevronLeft />
                              </IconButton>
                            </span>
                          </Tooltip>

                          <FormControl fullWidth size="small">
                            <InputLabel id="metric-date-label">Metric Date</InputLabel>
                            <Select
                              labelId="metric-date-label"
                              value={metricDate}
                              label="Metric Date"
                              onChange={(e) => setMetricDate(e.target.value as string)}
                              disabled={!metricDates.length}
                            >
                              <MenuItem value="">All dates</MenuItem>
                              {metricDates.map((date) => (
                                <MenuItem key={date} value={date}>{formatDisplayDate(date)}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          <Tooltip title="Next date">
                            <span>
                              <IconButton
                                size="small"
                                onClick={handleNextDate}
                                disabled={isNextDisabled()}
                                sx={{ border: '1px solid', borderColor: 'divider' }}
                              >
                                <ChevronRight />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="metric-level-label">Metric Level</InputLabel>
                          <Select
                            labelId="metric-level-label"
                            value={selectedLevelType}
                            label="Metric Level"
                            onChange={(e) => setSelectedLevelType(e.target.value as string)}
                          >
                            <MenuItem value="ALL">All Levels</MenuItem>
                            {availableLevelTypes.map((level) => (
                              <MenuItem key={level} value={level}>
                                {formatMetricLevelLabel(level)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Paper>

                  {projectMetrics.length > 0 && (
                    <Paper sx={{ p: 2, mb: 3 }}>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Project-Level Metrics
                      </Typography>
                      <Grid container spacing={2}>
                        {projectMetrics.map((m) => (
                          <Grid item xs={12} sm={6} md={4} key={m.computed_metric_id}>
                            <Card variant="outlined">
                              <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                  <Typography variant="subtitle2">
                                    {m.metric_name}
                                  </Typography>
                                  <Tooltip title="View Trend">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleOpenTrend(m)}
                                      sx={{ mt: -0.5, mr: -1 }}
                                    >
                                      <TrendingUp fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                                <Divider sx={{ my: 1 }} />
                                <Typography
                                  variant="h5"
                                  align="center"
                                  color={m.is_threshold_violated ? 'error.main' : 'success.main'}
                                >
                                  {formatMetricValue(m)}
                                </Typography>
                                {m.unit_of_measure && (
                                  <Typography variant="caption" color="text.secondary" display="block" align="center">
                                    {m.unit_of_measure}
                                  </Typography>
                                )}
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Paper>
                  )}

                  {scopedMetrics.length > 0 && (
                    <Paper sx={{ height: 600 }}>
                      <DataGrid
                        rows={scopedMetrics}
                        columns={columns}
                        getRowId={(row) => row.computed_metric_id}
                        pageSizeOptions={[25, 50, 100]}
                        disableRowSelectionOnClick
                      />
                    </Paper>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}

      <MetricTrendDialog
        open={trendDialogOpen}
        onClose={handleCloseTrend}
        metricName={selectedMetricForTrend?.metric_name || ''}
        metricIdentifierName={selectedMetricForTrend?.metric_identifier_name || null}
        reviewEntityName={selectedMetricForTrend?.review_entity_name || ''}
        metricType={selectedMetricForTrend?.metric_type || ''}
        valueType={selectedMetricForTrend?.value_type}
        unitOfMeasure={selectedMetricForTrend?.unit_of_measure}
        metricsType={selectedMetricForTrend?.threshold_direction}
        targetValue={selectedMetricForTrend?.target_value}
        trendData={trendData}
        loading={trendLoading}
      />
    </>
  );
};

export default ComputedMetricsContent;
